// frontend/src/App.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { getMe } from './api/client';
import RepDashboard from './components/RepDashboard';
import SalesLeaderDashboard from './components/SalesLeaderDashboard';
import FinanceDashboard from './components/FinanceDashboard';
import AdminDashboard from './components/AdminDashboard';
import ManagerView from './components/ManagerView';
import FinanceView from './components/FinanceView';
import DepartmentDashboard from './components/DepartmentDashboard';
import PseDashboard from './components/PseDashboard';
import LoginPage from './components/LoginPage';
import Layout from './components/Layout';
import { ThemeProvider } from './theme/ThemeContext';

// ─── Auth Context ─────────────────────────────────────────────────────────────
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMe()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <AppLoader />;

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function RequireAuth({ children, roles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.app_role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Which departments this user may open: their own, or all of them for the
// Operations / Finance departments and global admins. The server computes the
// list on /api/auth/me and enforces it on /api/departments; this mirrors it for
// routing and the sidebar.
export function visibleDepartmentsFor(user) {
  const own = user?.department || 'sales';
  return user?.visible_departments?.length ? user.visible_departments : [own];
}

// Sales team pages (Team View, Manager Comp): managers and finance by role, plus
// anyone who may page through departments (Operations / Finance departments).
export function canViewSalesTeam(user) {
  return ['manager', 'finance_admin', 'global_admin'].includes(user?.app_role)
    || visibleDepartmentsFor(user).length > 1;
}
function RequireSalesViewer({ children }) {
  const { user } = useAuth();
  if (!canViewSalesTeam(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

// Individual AE dashboards for a global admin looking at Sales from another
// department (Operations / Finance). Global admins IN Sales use My Dashboard.
export function canViewAeDashboards(user) {
  return user?.app_role === 'global_admin' && visibleDepartmentsFor(user).length > 1;
}
function RequireAeViewer({ children }) {
  const { user } = useAuth();
  if (!canViewAeDashboards(user)) return <Navigate to="/dashboard" replace />;
  return children;
}

// The PSE View page (Michael's plan) for anyone who may open Pre-Sales.
function RequirePresales({ children }) {
  const { user } = useAuth();
  if (!visibleDepartmentsFor(user).includes('presales')) return <Navigate to="/dashboard" replace />;
  return children;
}

// /departments/:code — only departments the user may see.
function RequireDepartment({ children }) {
  const { user } = useAuth();
  const { code } = useParams();
  if (!visibleDepartmentsFor(user).includes(code)) return <Navigate to="/dashboard" replace />;
  return children;
}

// "My Dashboard" resolves by role — global admins get a "View as" shell that can
// render any role's dashboard; managers get their team-driven commission view;
// finance admins get a company-wide overview (they have no ns_rep_id); reps keep
// the individual rep earnings dashboard. Everyone OUTSIDE Sales — any role — lands
// on their own department's dashboard; the Sales views are reached through the
// Sales lens in the sidebar (Ryan, Nov 2026).
function DashboardHome() {
  const { user } = useAuth();
  const dept = user?.department || 'sales';
  if (dept !== 'sales') {
    return <Navigate to={`/departments/${dept}`} replace />;
  }
  if (user?.app_role === 'global_admin')  return <AdminDashboard />;
  if (user?.app_role === 'manager')       return <SalesLeaderDashboard />;
  if (user?.app_role === 'finance_admin') return <FinanceDashboard />;
  return <RepDashboard />;
}

// ─── Dev Login ────────────────────────────────────────────────────────────────
// Visit http://localhost:3000/dev-login (NOT 3001)
// Goes through the CRA proxy so the cookie is stamped on port 3000,
// which is the same origin the frontend uses — no cross-port cookie problem.
function DevLogin() {
  const { setUser } = useAuth();
  const navigate    = useNavigate();
  // Forward every query param to the backend (role, plus optional repId to
  // masquerade as a specific rep, e.g. /dev-login?role=rep&repId=4988).
  const params = new URLSearchParams(window.location.search);
  if (!params.get('role')) params.set('role', 'finance_admin');
  const role = params.get('role');

  useEffect(() => {
    fetch(`/dev-login?${params.toString()}`, { credentials: 'include' })
      .then(() => fetch('/api/auth/me', { credentials: 'include' }))
      .then(res => res.ok ? res.json() : null)
      .then(user => {
        if (user) {
          setUser(user);
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      })
      .catch(() => navigate('/login', { replace: true }));
  }, []); // eslint-disable-line

  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg-base)',
      color: 'var(--text-primary)', fontFamily: 'sans-serif',
      flexDirection: 'column', gap: '12px',
    }}>
      <div className="loader-ring" />
      <p>Signing in as <b>{role}</b>...</p>
    </div>
  );
}

function AppLoader() {
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-base)', flexDirection: 'column', gap: '16px',
    }}>
      <div className="loader-ring" />
      <span style={{ color: '#5b9bd5', fontFamily: 'Montserrat, Arial, sans-serif', fontSize: '14px', letterSpacing: '0.1em' }}>
        LOADING
      </span>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
          <Route path="/login" element={<LoginPage />} />

          {process.env.NODE_ENV !== 'production' && (
            <Route path="/dev-login" element={<DevLogin />} />
          )}

          <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={
              <RequireAuth roles={['rep', 'manager', 'finance_admin', 'global_admin']}>
                <DashboardHome />
              </RequireAuth>
            } />
            <Route path="team" element={
              <RequireSalesViewer>
                <ManagerView />
              </RequireSalesViewer>
            } />
            <Route path="sales/manager-comp" element={
              <RequireSalesViewer>
                <SalesLeaderDashboard viewer />
              </RequireSalesViewer>
            } />
            <Route path="sales/overview" element={
              <RequireAuth roles={['finance_admin', 'global_admin']}>
                <FinanceDashboard />
              </RequireAuth>
            } />
            <Route path="sales/ae" element={
              <RequireAeViewer>
                <AdminDashboard initialMode="ae" />
              </RequireAeViewer>
            } />
            <Route path="finance" element={
              <RequireAuth roles={['finance_admin', 'global_admin']}>
                <FinanceView />
              </RequireAuth>
            } />
            <Route path="presales/pse" element={
              <RequirePresales>
                <PseDashboard viewer />
              </RequirePresales>
            } />
            <Route path="departments/:code" element={
              <RequireDepartment>
                <DepartmentDashboard />
              </RequireDepartment>
            } />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}